import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  List,
  ListItem,
  Text,
  VStack,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import { ExternalLinkIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { SPECIAL_PAGE_GA_CATEGORY } from '../common/constants';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import FullWidthBox from './FullWidthBox';
import ArticlesGrid from './ArticlesGrid';
import { Props as ArticleHeader } from './ArticleSummary';
import { HeroAreaSettings, HeroArea } from './HeroArea';
import { MessageAreaSettings, MessageArea } from './MessageArea';
import ViewAllLink from './ViewAllLink';
import SpecialPageLinks, { SpecialPageLinkItem } from './SpecialPageLinks';
import SpecialPageDescription, {
  DescriptionSettings,
} from './SpecialPageDescription';

/**
 * S3パケットに保存する"compilation"テンプレート専用JSONデータの型
 */
export type CompilationSettings = {
  templateName: 'compilation';
  heroContent: HeroAreaSettings;
  description?: DescriptionSettings;
  message: MessageAreaSettings;
  compilation: {
    tableOfContents?: {
      title: string;
      emptyListMessage: string;
    };
    chapters: {
      title: string;
      description: string | string[]; // 項目の説明は通常のテキスト or 配列を設定すると・をつけたリストになる
      note?: string; // スライドリストのタイトルの横に注釈を入れる場合
      articleIds?: string[];
      externalArticles?: SpecialPageLinkItem[];
    }[];
    noChaptersNumber?: boolean;
  };
  initialShowNumber?: number; // 「もっと見る」ボタンを押す前に表示されている件数
  ending?: {
    text: string; // markdown形式
    link: {
      url: string;
      caption: string;
      label: string;
    };
  };
};

/**
 * CompilationPageContentコンポーネントに渡すpropsの型
 */
export type CompilationContentProps = CompilationSettings & {
  specialArticleId: string;
  articles: ArticleHeader[];
  isLicenseVerifiedDoctor: boolean;
  isAllowAccount: boolean;
  isMobile: boolean;
};

function hasChapterContent({
  articleIds,
  externalArticles,
}: {
  articleIds?: string[];
  externalArticles?: SpecialPageLinkItem[];
}): boolean {
  return !!articleIds?.length || !!externalArticles?.length;
}

function TableOfContents({
  title,
  emptyListMessage,
  chapters,
  noChaptersNumber,
  isMobile,
}: {
  title: string;
  emptyListMessage: string;
  chapters: {
    title: string;
    description: string | string[];
    note?: string;
    articleIds?: string[];
    externalArticles?: SpecialPageLinkItem[];
  }[];
  noChaptersNumber?: boolean;
  isMobile: boolean;
}) {
  const descriptionFontSize = 14;

  const themeTextMaxLength = Math.max(
    ...chapters.map(({ title }) => title.length),
  );
  const themeColumnWidth = (themeTextMaxLength + 2) * descriptionFontSize;

  const emptyThemeTextMaxLength = Math.max(
    ...chapters
      .filter((chapter) => !hasChapterContent(chapter))
      .map(({ title }) => title.length),
  );
  const emptyThemeColumnWidth =
    (emptyThemeTextMaxLength + 4) * descriptionFontSize;

  const onClickListItem = (targetId: string) => {
    const target = document.getElementById(targetId);
    if (target) {
      window.scrollTo({
        top: target.offsetTop - 70, // ヘッダーの高さ分調整
        behavior: 'smooth',
      });
    }
  };

  return (
    <Box backgroundColor="white" py={4} px={isMobile ? 2 : 8} w="full">
      <Box fontSize="xs" fontWeight="bold">
        {title}
      </Box>
      <List py={1}>
        {chapters.map(
          ({ title, description, articleIds, externalArticles }, index) => {
            const hasArticle = hasChapterContent({
              articleIds,
              externalArticles,
            });

            return (
              <>
                {hasArticle ? (
                  <ListItem
                    key={title}
                    py={1}
                    fontSize="sm"
                    display="flex"
                    flexDirection={isMobile ? 'column' : 'row'}
                    alignItems="flex-start"
                    onClick={() => onClickListItem(title)}
                    cursor="pointer"
                  >
                    <Flex
                      color="primaryDark"
                      fontWeight="bold"
                      w={themeColumnWidth}
                      flexShrink={0}
                      gap={1}
                    >
                      {!noChaptersNumber && <Text>{index + 1}.</Text>}
                      <Text textDecoration="underline">{title}</Text>
                    </Flex>
                    <Flex
                      flexWrap="wrap"
                      justifyContent="flex-start"
                      ml={isMobile ? '1em' : 0}
                    >
                      {Array.isArray(description) ? (
                        <List>
                          {description.map((text) => (
                            <ListItem
                              key={text}
                              display={isMobile ? 'list-item' : 'inline-block'}
                              pr={1}
                            >
                              ・{text}
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Text>{description}</Text>
                      )}
                    </Flex>
                  </ListItem>
                ) : (
                  <ListItem
                    key={title}
                    py={1}
                    fontSize="sm"
                    display="flex"
                    alignItems="flex-start"
                  >
                    <Flex
                      w={isMobile ? emptyThemeColumnWidth : themeColumnWidth}
                      flexShrink={0}
                      gap={1}
                      color="textDisabled"
                    >
                      {!noChaptersNumber && `${index + 1}. `}
                      {title}
                    </Flex>
                    <Box>{emptyListMessage}</Box>
                  </ListItem>
                )}
              </>
            );
          },
        )}
      </List>
    </Box>
  );
}

function ChapterHeader({
  index,
  title,
  note,
  isMobile,
  noChaptersNumber,
}: {
  index: number;
  title: string;
  note?: string;
  isMobile: boolean;
  noChaptersNumber?: boolean;
}) {
  return (
    <Flex
      gap={2}
      mb={4}
      alignItems={isMobile ? 'flex-start' : 'center'}
      flexDirection={isMobile ? 'column' : 'row'}
    >
      <Heading
        id={title}
        as="h3"
        fontSize={isMobile ? 'lg' : 'xl'}
        fontWeight="bold"
        p={2}
        pl={4}
        borderLeft="4px solid"
        borderColor="primaryMain"
      >
        {!noChaptersNumber && `${index + 1}. `}
        {title}
      </Heading>
      {note && (
        <Box fontSize="sm" color="textGray">
          {note}
        </Box>
      )}
    </Flex>
  );
}

function ChapterArticles({
  specialArticleId,
  articles,
  isMobile,
  initialShowNumber,
}: {
  specialArticleId: string;
  articles: ArticleHeader[];
  isMobile: boolean;
  initialShowNumber?: number;
}) {
  const [showNumber, setShowNumber] = useState<number | undefined>(
    initialShowNumber,
  );
  const shouldShowShowMoreButton = !!showNumber && showNumber < articles.length;
  const readMoreNumber = !!showNumber ? articles.length - showNumber : 0;
  const readMoreLabel = (
    <>
      <Text pr={2}>もっと見る ({readMoreNumber})</Text>
      <ChevronDownIcon />
    </>
  );

  const onClickShowMoreButton = () =>
    setShowNumber((prev) =>
      !!prev && !!initialShowNumber ? prev + initialShowNumber : undefined,
    );

  const showArticles = !!showNumber ? articles.slice(0, showNumber) : articles;

  return (
    <>
      <ArticlesGrid
        articles={showArticles}
        isMobile={isMobile}
        gaEventLabel={`${specialArticleId}_articles`}
        onShowThumbnail={(articleId) => {
          sendAnalyticsEvent(
            'slide_thumbnail',
            'show',
            `special_${specialArticleId}#${articleId}`,
          );
        }}
        onClick={(articleId) => {
          sendAnalyticsEvent(
            'slide_thumbnail',
            'click',
            `special_${specialArticleId}#${articleId}`,
          );
        }}
      />
      {shouldShowShowMoreButton && (
        <ViewAllLink
          href="javascript:void(0);"
          onClick={onClickShowMoreButton}
          label={readMoreLabel}
        />
      )}
    </>
  );
}

function Ending({
  specialArticleId,
  text,
  link: { url, caption, label },
  isMobile,
}: {
  specialArticleId: string;
  text: string;
  link: {
    url: string;
    caption?: string;
    label: string;
  };
  isMobile: boolean;
}) {
  return (
    <Flex
      gap={4}
      alignItems="center"
      py={8}
      px={isMobile ? 2 : 10}
      flexDirection={isMobile ? 'column' : 'row'}
    >
      <Box fontSize="md">
        <ReactMarkdown>{text}</ReactMarkdown>
      </Box>
      <VStack fontSize="sm" ml={2} gap={1}>
        {caption && <Box fontWeight="bold">{caption}</Box>}
        <Button
          as="a"
          colorScheme="brand"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          display="flex"
          gap={1}
          onClick={() =>
            sendAnalyticsEvent(
              SPECIAL_PAGE_GA_CATEGORY,
              'click',
              `${specialArticleId}_ending_${label}`,
            )
          }
        >
          {label}
          <ExternalLinkIcon />
        </Button>
      </VStack>
    </Flex>
  );
}

/**
 * スライド選集系 特集ページテンプレート
 *
 * 先生方主導で有益なスライドを特集
 */
export function CompilationPageContent({
  specialArticleId,
  heroContent,
  description,
  message,
  articles,
  isMobile,
  compilation,
  initialShowNumber,
  ending,
}: CompilationContentProps) {
  return (
    <>
      <HeroArea {...heroContent} isMobile={isMobile} />
      <FullWidthBox bg="#EEF6ED">
        <Container>
          <VStack spacing={2} py={4}>
            <SpecialPageDescription
              {...description}
              containerProps={{
                py: 4,
                px: isMobile ? 4 : 8,
                backgroundColor: 'white',
              }}
            />
            <MessageArea
              {...message}
              specialArticleId={specialArticleId}
              isMobile={isMobile}
            />
            {compilation.tableOfContents && (
              <TableOfContents
                {...compilation.tableOfContents}
                chapters={compilation.chapters}
                isMobile={isMobile}
                noChaptersNumber={compilation.noChaptersNumber}
              />
            )}
          </VStack>
        </Container>
      </FullWidthBox>
      <Box px={4}>
        {
          // articlesまたはexternalArticlesをchapterごとに振り分けて表示
          compilation.chapters.map(
            ({ title, note, articleIds, externalArticles }, index) => {
              let chapterContent: React.ReactNode = null;

              const chapterArticles = articleIds
                ?.map((id) =>
                  articles.find(({ articleId }) => articleId === id),
                )
                .filter((article) => !!article) as ArticleHeader[] | undefined;
              if (chapterArticles?.length) {
                chapterContent = (
                  <ChapterArticles
                    specialArticleId={specialArticleId}
                    articles={chapterArticles}
                    isMobile={isMobile}
                    initialShowNumber={initialShowNumber}
                  />
                );
              } else if (externalArticles?.length) {
                chapterContent = (
                  <SpecialPageLinks
                    links={externalArticles}
                    isMobile={isMobile}
                    maxHeight="auto"
                    grid={[1, 2, 3]}
                    gaEventLabelPrefix={`${specialArticleId}_extArticles`}
                  />
                );
              }
              return !!chapterContent ? (
                <Box key={title} my={8}>
                  <ChapterHeader
                    index={index}
                    title={title}
                    note={note}
                    noChaptersNumber={compilation.noChaptersNumber}
                    isMobile={isMobile}
                  />
                  {chapterContent}
                </Box>
              ) : null;
            },
          )
        }
      </Box>
      {ending && (
        <FullWidthBox bg="#EEF6ED" mt={16} mb={-16}>
          <Container>
            <Ending
              {...ending}
              isMobile={isMobile}
              specialArticleId={specialArticleId}
            />
          </Container>
        </FullWidthBox>
      )}
    </>
  );
}
